import React, { useCallback, useContext, useState } from "react";
import { Box, Button, Grid, Divider, Stack } from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  AdminPanelSettings as AttachIcon,
} from "@mui/icons-material";

import boxStyles from "../../../styles/boxing";
import MainTitle from "../../../Components/Common/MainTitle/MainTitle";
import TextFieldRenderer from "../../../Components/Form/TextFieldRenderer";

import { DELETE_CLUB, GET_CLUBS } from "../../../graphql/clubGraphql";

import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "../../../context/Internationalization";
import { SnackBarContext } from "../../../context/SnackBarContext";

import useInfinityScroll from "../../../Hooks/useInfinityScroll";
import useGraphql from "../../../Hooks/useGraphql";
import useLog from "../../../Hooks/useLog";
import { getStorage } from "../../../auth/utils";
import { useDebouncedSearch } from "../../../Hooks/useDebouncedSearch";

import ClubsTable from "./ClubsTable/ClubsTable";
import ClubsDrawer from "./ClubsDrawer/ClubsDrawer";
import ClubChooseConfirmationModal from "./Components/ClubChooseConfirmationModal/ClubChooseConfirmationModal";
import DeleteConfirmationModal from "./Components/DeleteConfirmationModal/DeleteConfirmationModal";

const tableMenuActions = (handleAction, handleChooseClub, t) => [
  {
    label: t("edit_club"),
    icon: <EditIcon fontSize="small" />,
    onClickAction: (club) => handleAction("edit", club),
  },
  {
    label: t("choose_club"),
    icon: <AttachIcon fontSize="small" />,
    onClickAction: (club) => handleChooseClub(club),
  },
  {
    label: t("delete_club"),
    icon: <DeleteIcon sx={{ color: "red" }} fontSize="small" />,
    onClickAction: (club) => handleAction("delete", club),
  },
];

const SuperAdminClubs = () => {
  const auth = useAuthContext();
  const user = auth.authState.user;
  const { t } = useTranslation();
  const snackBar = useContext(SnackBarContext);
  const { axiosGraphQL } = useGraphql();
  const { errorLog } = useLog();

  const [searchTerm, setSearchTerm] = useState("");
  const [extraClubs, setExtraClubs] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [state, setState] = useState({
    drawer: false,
    chooseClubModal: false,
    deleteModal: false,
    selectedClub: null,
    action: "",
    loading: false,
  });

  const fetchClubs = useCallback(
    async (term) => {
      const res = await axiosGraphQL({
        query: GET_CLUBS,
        variables: { term, skip: 0, limit: 20 },
      });
      const results = res.data.data.getClubs || [];

      setExtraClubs([]);
      setHasMore(results.length === 20);
      return results;
    },
    [axiosGraphQL]
  );

  const {
    options: clubs,
    loading,
    debouncedTerm,
    refetch,
  } = useDebouncedSearch({
    searchTerm,
    fetchOptions: fetchClubs,
  });

  const loadMoreClubs = useCallback(async () => {
    try {
      const currentSkip = clubs.length + extraClubs.length;

      const response = await axiosGraphQL({
        query: GET_CLUBS,
        variables: {
          term: debouncedTerm,
          skip: currentSkip,
          limit: 20,
        },
      });

      const more = response.data.data.getClubs || [];

      // Deduplicate
      const existingIds = new Set([
        ...clubs.map((c) => c._id),
        ...extraClubs.map((c) => c._id),
      ]);
      const newClubs = more.filter((club) => !existingIds.has(club._id));

      setExtraClubs((prev) => [...prev, ...newClubs]);

      // Stop only if the last fetch was smaller than limit
      setHasMore(more.length === 20);
    } catch (err) {
      errorLog(err, "SuperAdminClubs", "loadMoreClubs", "GET_CLUBS");
    }
  }, [debouncedTerm, clubs, extraClubs, axiosGraphQL]);

  const lastElementRef = useInfinityScroll({
    updatePage: loadMoreClubs,
    isLoading: loading,
    hasMore,
  });

  const allClubs = [
    ...new Map([...clubs, ...extraClubs].map((c) => [c._id, c])).values(),
  ];

  const onChangeFilter = ({ target: { name, value } }) => {
    if (name === "term") {
      setSearchTerm(value);
    }
  };

  const handleOpenDrawer = () => setState({ ...state, drawer: true });
  const handleCloseDrawer = () =>
    setState({ ...state, drawer: false, selectedClub: null });

  const handleAction = (action, club) => {
    if (action === "delete") {
      setState({ ...state, action, deleteModal: true, selectedClub: club });
    } else {
      setState({ ...state, action, drawer: true, selectedClub: club });
    }
  };

  const handleChooseClub = (club) => {
    setState({ ...state, chooseClubModal: true, selectedClub: club });
  };

  const updateSelectedClub = () => {
    auth.setAuthState({
      user: { ...user },
      club: state.selectedClub,
      token: getStorage().getItem("token"),
    });
    setState({ ...state, chooseClubModal: false, selectedClub: null });
  };

  const handleCloseDeleteModal = () => {
    setState({ ...state, deleteModal: false, selectedClub: null });
  };

  const handleDeleteClub = async () => {
    try {
      setState((prev) => ({ ...prev, loading: true }));

      await axiosGraphQL({
        query: DELETE_CLUB,
        variables: { clubId: state.selectedClub?._id },
      });

      snackBar.setOptions({
        alertMessage: `${t("the_club")} ${t("deleted_successfully")}`,
        alertSeverity: "success",
      });
      snackBar.setSnackbarOpen(true);
      refetch();
    } catch (err) {
      errorLog(err, "SuperAdminClubs", "handleDeleteClub", "DELETE_CLUB");
      snackBar.setOptions({
        alertMessage: err.message,
        alertSeverity: "error",
      });
      snackBar.setSnackbarOpen(true);
    } finally {
      setState((prev) => ({
        ...prev,
        loading: false,
        deleteModal: false,
      }));
    }
  };

  return (
    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={boxStyles.headerContainer} component="div">
            <Stack direction="row" spacing={2}>
              <MainTitle title={t("clubs_admin_panel")} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                type="button"
                size="small"
                variant="contained"
                color="primary"
                onClick={handleOpenDrawer}
              >
                {t("new_club")}
              </Button>
            </Stack>
          </Box>
          <Divider />
        </Grid>

        <Grid item xs={12} style={boxStyles.filtersContainer}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextFieldRenderer
                label={t("search")}
                onChange={onChangeFilter}
                name="term"
                value={searchTerm}
              />
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12}>
          {allClubs.length === 0 && !loading ? (
            <span>{t("no_clubs_found")}</span>
          ) : (
            <ClubsTable
              lastElementRef={lastElementRef}
              clubs={allClubs}
              menuActions={tableMenuActions(handleAction, handleChooseClub, t)}
              loading={loading}
            />
          )}
        </Grid>
      </Grid>

      <ClubsDrawer
        openDrawer={state.drawer}
        onCloseDrawer={handleCloseDrawer}
        selectedClub={state.selectedClub}
        getClubs={() => refetch()}
      />

      <ClubChooseConfirmationModal
        open={state.chooseClubModal}
        onClose={() => setState({ ...state, chooseClubModal: false })}
        onSave={updateSelectedClub}
        clubName={state.selectedClub?.name}
      />

      <DeleteConfirmationModal
        open={state.deleteModal}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteClub}
      />
    </Box>
  );
};

export default SuperAdminClubs;
